<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/vue-3';
import { useApolloClient } from '@vue/apollo-composable';

import { openLightboxFromImage } from '../../UImageLightbox/lightbox-store';
import { clearUpload, getUploadState } from './upload-registry';
import { holdBinaryFileUrls, refreshBinaryFileUrls, resolveBinaryFileUrls } from './useBinaryFileHydration';

const props = defineProps<NodeViewProps>();

const { client } = useApolloClient();

const fileId = computed(() => props.node.attrs.fileId as string | null);
const uploadId = computed(() => props.node.attrs.uploadId as string | null);

const uploadState = computed(() => getUploadState(uploadId.value));
const hydrated = computed(() => (fileId.value ? resolveBinaryFileUrls(client, fileId.value) : null));

// While this view shows the file, its URLs are renewed in the background.
watch(fileId, (id, _previous, onCleanup) => {
  if (id) {
    onCleanup(holdBinaryFileUrls(id));
  }
}, { immediate: true });

const isUploading = computed(() => !fileId.value && uploadState.value?.status === 'uploading');
const uploadProgress = computed(() => uploadState.value?.progress ?? 0);

/**
 * Display source priority: local object-URL preview while uploading → hydrated
 * presigned URL for managed files → raw src for external (non-managed) images.
 */
const displaySrc = computed(() => {
  if (uploadState.value?.previewUrl) {
    return uploadState.value.previewUrl;
  }

  if (fileId.value) {
    return hydrated.value?.status === 'ready' ? hydrated.value.url ?? null : null;
  }

  const src = props.node.attrs.src as string | null;

  // Sentinel src without hydration means the fileId attr was lost — treat as broken.
  return src && !src.startsWith('omb-file://') ? src : null;
});

/**
 * The URL this node tried and could not render after the retry budget was
 * spent — typically a format the browser can't decode (HEIC from an iPhone).
 * Keyed on the URL, not a flag: another node sharing the file may be mid-refresh,
 * and the fresh URL it brings deserves its own attempt here.
 */
const failedSrc = ref<string | null>(null);

const isResolving = computed(() => Boolean(fileId.value) && hydrated.value?.status === 'pending');

/** Shown as an error card with a download link instead of a broken image. */
const loadFailed = computed(() => failedSrc.value !== null && failedSrc.value === displaySrc.value);

const isError = computed(() => {
  if (isUploading.value || isResolving.value) {
    return false;
  }

  if (uploadState.value?.status === 'error' || loadFailed.value) {
    return true;
  }

  if (fileId.value) {
    return hydrated.value?.status === 'error';
  }

  return displaySrc.value === null;
});

const errorMessage = computed(() => {
  if (uploadState.value?.errorMessage) {
    return uploadState.value.errorMessage;
  }

  if (loadFailed.value) {
    return 'This image can\'t be displayed in the browser.';
  }

  return 'Failed to load the image.';
});

/**
 * A presigned URL may expire while the document is open — refetch once. If the
 * fresh URL fails too, the file itself is unrenderable: stop and show the card.
 */
const onImageError = (event: Event) => {
  if (!fileId.value || uploadState.value?.previewUrl) {
    return;
  }

  const attemptedSrc = (event.target as HTMLImageElement).getAttribute('src');

  if (!refreshBinaryFileUrls(client, fileId.value)) {
    failedSrc.value = attemptedSrc;
  }
};

const onRemove = () => {
  if (uploadId.value) {
    clearUpload(uploadId.value);
  }

  props.deleteNode();
};

/**
 * A local upload preview has no stable URL yet and its node is about to be
 * replaced — only settled images take part in the lightbox.
 */
const isViewable = computed(() => Boolean(displaySrc.value) && !isUploading.value);

const openPreview = (image: HTMLImageElement | null) => {
  if (!isViewable.value || !image) {
    return;
  }

  openLightboxFromImage(image);
};

/**
 * Click vs. double-click: in a read-only document a single click opens the image,
 * while in the editor a click has to stay available for selecting the node, so
 * the preview moves to a double-click. Read-only, the image sits in a real
 * button, so the keyboard opens it too.
 */
const opensOnClick = computed(() => isViewable.value && !props.editor.isEditable);

const onPreviewClick = (event: MouseEvent) => {
  if (!opensOnClick.value) {
    return;
  }

  openPreview((event.currentTarget as HTMLElement).querySelector('img'));
};

const onImageDoubleClick = (event: MouseEvent) => {
  if (!props.editor.isEditable) {
    return;
  }

  openPreview(event.currentTarget as HTMLImageElement);
};

const downloadUrl = computed(() =>
  (fileId.value && hydrated.value?.status === 'ready' ? hydrated.value.downloadUrl ?? null : null));
</script>

<template>
  <NodeViewWrapper
    class="my-2"
    data-testid="editor-attachment-image"
  >
    <!-- Error card -->
    <div
      v-if="isError"
      class="flex items-center gap-3 rounded-[10px] border border-error/40 bg-error/10 p-3"
    >
      <UIcon
        name="i-lucide-image-off"
        class="size-5 text-error shrink-0"
      />
      <p class="text-sm text-error min-w-0 truncate">
        {{ errorMessage }}
      </p>
      <UButton
        v-if="loadFailed && downloadUrl"
        :to="downloadUrl"
        target="_blank"
        size="xs"
        color="neutral"
        variant="outline"
        icon="i-lucide-download"
        :label="(props.node.attrs.alt as string) || 'Download'"
        class="shrink-0 max-w-60"
      />
      <button
        v-if="props.editor.isEditable"
        type="button"
        class="ml-auto shrink-0 text-error hover:text-error/80 transition-colors p-1"
        title="Remove"
        @click="onRemove"
      >
        <UIcon
          name="i-lucide-x"
          class="size-4"
        />
      </button>
    </div>

    <!-- Resolving skeleton -->
    <div
      v-else-if="!displaySrc"
      class="h-40 max-w-md animate-pulse rounded-lg bg-accented"
    />

    <!-- Image (uploading preview or resolved) -->
    <div
      v-else
      class="relative inline-block max-w-full"
    >
      <component
        :is="opensOnClick ? 'button' : 'div'"
        :type="opensOnClick ? 'button' : undefined"
        :aria-label="opensOnClick ? `Open ${(props.node.attrs.alt as string) || 'image'}` : undefined"
        class="block max-w-full rounded-lg"
        :class="opensOnClick ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary' : ''"
        @click="onPreviewClick"
      >
        <img
          data-testid="editor-attachment-image-visual"
          :src="displaySrc"
          :alt="(props.node.attrs.alt as string) || ''"
          class="max-w-full max-h-[32rem] rounded-lg"
          :class="[
            isUploading ? 'opacity-60' : '',
            props.selected && props.editor.isEditable ? 'ring-2 ring-primary' : '',
            isViewable ? 'cursor-zoom-in' : '',
          ]"
          :data-lightbox="isViewable ? '' : undefined"
          :data-lightbox-download="downloadUrl ?? undefined"
          :data-lightbox-name="(props.node.attrs.alt as string) || undefined"
          :data-lightbox-file-id="fileId ?? undefined"
          :title="isViewable ? (props.editor.isEditable ? 'Double-click to open' : 'Click to open') : undefined"
          draggable="false"
          @error="onImageError"
          @dblclick="onImageDoubleClick"
        >
      </component>

      <!-- Upload progress overlay -->
      <div
        v-if="isUploading"
        class="absolute inset-x-3 bottom-3 h-1.5 overflow-hidden rounded-full bg-default/60"
      >
        <div
          class="h-full rounded-full bg-primary transition-[width]"
          :style="{ width: `${uploadProgress}%` }"
        />
      </div>
    </div>
  </NodeViewWrapper>
</template>
